import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { User } from '~/auth/model/user';
import { CONNECT_ISSUER } from '~/auth/providers/connect.provider';
import { AuthService } from '~/auth/services/auth/auth.service';
import { UserService } from '~/auth/services/user/user.service';
import { APP_STORAGE } from '~/app.token';
import { AttendeeMenuLinkCms } from '~/cms/interfaces/attendee-menu.cms.interface';
import { ConfigurationCms } from '~/cms/interfaces/configuration.interface';
import { CmsService } from '~/cms/services/cms/cms.service';
import { MenuItemInterface } from '~/component/header/login/menu/interfaces/menu-item.interface';
import { NavigationTabBarButtonConfigInterface } from '~/component/navigation-tab-bar/interfaces/button.config.interface';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { ParamethorNameEnum } from '~/paramethor/names.enum';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { TalkService } from '~/messaging/services/talk.service';

const OUTAGE_BANNER_CLOSED_KEY = 'onlinePaymentOutageBannerClosed';

@Component({
  selector: 'app-attendee-header',
  templateUrl: './header.component.html',
})
export class AttendeeHeaderComponent implements OnInit, OnDestroy {
  private readonly subscriptions$ = new Subscription();
  public headerDropdownMenuItems: MenuItemInterface[][];
  public homeLink: string;
  public links: AttendeeMenuLinkCms[] = [];
  public tabBarButtonsConfig: NavigationTabBarButtonConfigInterface[];
  public user: User;
  public configuration: ConfigurationCms;
  public isOutageBannerClosed = false;
  public readonly closeIconType = VtmnPlayIconTypeEnum.Close;
  constructor(
    @Inject(CONNECT_ISSUER) private readonly connectIssuer: string,
    @Inject(TranslateService)
    private readonly translateService: TranslateService,
    private readonly authService: AuthService,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly talkService: TalkService,
    private readonly cmsService: CmsService,
    @Inject(APP_STORAGE) private readonly storage: Storage,
    private readonly paramethorService: ParamethorService
  ) {
    this.isOutageBannerClosed =
      this.storage?.getItem(OUTAGE_BANNER_CLOSED_KEY) === 'true';
  }

  public closeOutageBanner(): void {
    this.isOutageBannerClosed = true;
    this.storage?.setItem(OUTAGE_BANNER_CLOSED_KEY, 'true');
  }

  public get showOnlinePaymentOutageBanner(): boolean {
    return this.paramethorService.isTrue(
      ParamethorNameEnum.OnlinePaymentOutageBanner
    );
  }

  private get isUserFed(): boolean {
    return this.userService.getUser()?.providerName === 'FED';
  }

  public ngOnDestroy(): void {
    this.subscriptions$?.unsubscribe();
  }

  public ngOnInit(): void {
    this.homeLink = this.localizeRouterService.translateRoute('/').toString();

    this.subscriptions$.add(
      this.cmsService.getConfiguration().subscribe({
        next: (configuration) => {
          this.configuration = configuration;
          this.tabBarButtonsConfig = [
            {
              iconName: VtmnPlayIconTypeEnum.Question,
              href: this.configuration?.header_help_center_url,
              isConversion: false,
            },
          ];

          if (this.configuration?.show_header_become_partner_url) {
            this.tabBarButtonsConfig.unshift({
              text: this.translateService.instant(
                'attendee.header.tab_bar.link.become_organizer'
              ),
              isConversion: false,
              href: this.configuration?.header_become_partner_url,
            });
          }

          this.headerDropdownMenuItems = [
            [
              {
                label: this.translateService.instant(
                  'attendee.header.login_menu.link.booking'
                ),
                routerLink: this.localizeRouterService
                  .translateRoute('/attendee.bookings')
                  .toString(),
              },
              {
                label: this.translateService.instant(
                  'attendee.header.login_menu.link.messages'
                ),
                routerLink: this.localizeRouterService
                  .translateRoute('/attendee.messages.inbox')
                  .toString(),
              },
              {
                label: this.translateService.instant(
                  'attendee.header.login_menu.link.help_center'
                ),
                href: this.configuration?.header_help_center_url,
              },
            ],
            [
              {
                label: this.translateService.instant(
                  'attendee.header.login_menu.link.my_account'
                ),
                href: 'https://account.decathlon.com/',
                icon: VtmnPlayIconTypeEnum.ExternalLink,
              },
            ],
          ];
        },
      })
    );

    this.subscriptions$.add(
      this.cmsService.getAttendeeMenu().subscribe((menu) => {
        this.links = menu.left_link_collection;
      })
    );

    this.subscriptions$.add(
      this.userService.isAuthenticated().subscribe((isAuthenticated) => {
        this.user = this.userService.getUser();

        if (isAuthenticated) {
          this.subscriptions$.add(
            this.userService
              .hasOrganizations()
              .subscribe((hasOrganizations) => {
                if (
                  hasOrganizations &&
                  this.headerDropdownMenuItems[1][0]?.label !==
                    this.translateService.instant(
                      'attendee.header.login_menu.organizer.dashboard'
                    )
                ) {
                  this.headerDropdownMenuItems.splice(1, 0, [
                    {
                      label: this.translateService.instant(
                        'attendee.header.login_menu.organizer.dashboard'
                      ),
                      href: this.localizeRouterService
                        .translateRoute('/organizer.planning.planning')
                        .toString(),
                      icon: VtmnPlayIconTypeEnum.ExternalLink,
                    },
                  ]);
                }
              })
          );

          this.tabBarButtonsConfig = [
            {
              iconName: VtmnPlayIconTypeEnum.Calendar,
              text: this.translateService.instant(
                'attendee.header.tab_bar.link.booking'
              ),
              isConversion: false,
              routerLink: this.localizeRouterService
                .translateRoute('/attendee.bookings')
                .toString(),
            },
            {
              iconName: VtmnPlayIconTypeEnum.Message,
              text: this.translateService.instant(
                'attendee.header.tab_bar.link.messages'
              ),
              totalNotifications: undefined,
              isConversion: false,
              routerLink: this.localizeRouterService
                .translateRoute('/attendee.messages.inbox')
                .toString(),
            },
          ];

          // mount inbox, create user and set unread notification
          this.subscriptions$.add(
            this.talkService
              .createCurrentSession()
              .pipe(
                map(() => {
                  this.getMessageButton();
                })
              )
              .subscribe()
          );
        }
      })
    );
  }

  private getMessageButton(): void {
    this.talkService.onNotification$().subscribe((unread) => {
      const messageButton = this.tabBarButtonsConfig.find(
        (button) => button.iconName === VtmnPlayIconTypeEnum.Message
      );

      if (messageButton) {
        messageButton.totalNotifications = unread;
      }
    });
  }

  public login(): void {
    this.authService.login(this.connectIssuer, {
      from: this.router.url,
      lang: this.translateService.currentLang,
    });
  }
}
